import { ref, watch, computed, nextTick, provide } from 'vue'
import api from '@/services/api'

function extractEntityInfo (obj) {
  if (!obj || typeof obj !== 'object') {
    return { id: null, name: 'Item', nameLabel: 'Item' }
  }
  const keys = Object.keys(obj)
  let id = null
  let name = 'Item'
  let nameLabel = 'Item'

  const idKey = keys.find(key => key.toLowerCase().startsWith('id'))
  if (idKey) {
    id = obj[idKey]
  }

  let nameKey = keys.find(key => key.toLowerCase().includes('nome'))
  if (nameKey) {
    name = obj[nameKey]
    nameLabel = 'Nome'
  } else {
    nameKey = keys.find(key => key.toLowerCase() === 'name')
    if (nameKey) {
      name = obj[nameKey]
      nameLabel = 'Name'
    } else {
      nameKey = keys.find(key => key.toLowerCase().includes('descricao'))
      if (nameKey) {
        name = obj[nameKey]
        nameLabel = 'Descrição'
      }
    }
  }
  return { id, name, nameLabel }
}

/**
 * Extrai os campos de auditoria do objeto, normalizando as chaves
 * para o formato camelCase usado pelo FormularioCadastro.
 */
function extractAuditInfo (obj) {
  if (!obj || typeof obj !== 'object') return null
  return {
    nomeUsuarioCriacao: obj.nomeUsuarioCriacao || obj.NomeUsuarioCriacao || null,
    tsCriacao: obj.tsCriacao || obj.TsCriacao || null,
    nomeUsuarioModificacao: obj.nomeUsuarioModificacao || obj.NomeUsuarioModificacao || null,
    tsModificacao: obj.tsModificacao || obj.TsModificacao || null
  }
}

export function useFormulario (props, emit, options = {}) {
  const { mapaCampos = {} } = options

  const item = ref({})
  const isLoading = ref(false)
  const hasChanges = ref(false)
  const errors = ref({})
  let originalItemJson = ''

  // GUARDIÃO: A Magia da Injeção de Dependência!
  // Qualquer componente filho nesta tela terá acesso direto a este objeto de erros.
  provide('formErrors', errors)

  const entityInfo = computed(() => extractEntityInfo(item.value))

  // GUARDIÃO: Informações de auditoria extraídas automaticamente do item.
  // Disponibilizadas via provide para o FormularioCadastro consumir sem
  // que os componentes-pai precisem passar props explicitamente.
  const auditInfo = computed(() => extractAuditInfo(item.value))
  provide('auditInfo', auditInfo)

  const isEditing = computed(() => {
    const idKey = options.idKey || Object.keys(item.value).find(key => {
      const k = key.toLowerCase()
      return k.startsWith('id') && k !== 'idempresa' && k !== 'idtenant'
    })
    return !!(idKey && item.value[idKey] && item.value[idKey] !== 0)
  })

  const initializeForm = (customItem = null) => {
    const defaults = JSON.parse(JSON.stringify(props.modeloVazio || {}))
    if (customItem) {
      item.value = { ...defaults, ...JSON.parse(JSON.stringify(customItem)) }
    } else if (props.itemParaEditar) {
      item.value = { ...defaults, ...JSON.parse(JSON.stringify(props.itemParaEditar)) }
    } else {
      item.value = defaults
    }
    originalItemJson = JSON.stringify(item.value)
    hasChanges.value = false
    errors.value = {}
  }

  watch(() => props.itemParaEditar, () => initializeForm(), { immediate: true })

  watch(item, (newItem) => {
    const newItemJson = JSON.stringify(newItem)
    if (originalItemJson !== '' && newItemJson !== originalItemJson) {
      hasChanges.value = true
      emit('changesDetected', true)
    } else if (newItemJson === originalItemJson) {
      hasChanges.value = false
      emit('changesDetected', false)
    }
  }, { deep: true })

  const focarPrimeiroErro = async () => {
    await nextTick()

    setTimeout(() => {
      const elementoComErro = document.querySelector('.is-invalid')
      if (elementoComErro) {
        elementoComErro.focus()
        elementoComErro.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 50)
  }

  const validarCamposObrigatoriosFrontEnd = () => {
    let isValid = true
    const frontendErrors = {}

    const labelsObrigatorios = document.querySelectorAll('label.is-required')

    console.log(`[DEBUG useFormulario] Iniciando validação de ${labelsObrigatorios.length} campos obrigatórios.`)

    labelsObrigatorios.forEach(label => {
      // Se o label ou seu container estiver invisível (display: none via v-show p. ex.), ignora a validação
      if (label.offsetParent === null) return

      const inputId = label.getAttribute('for')
      if (!inputId) {
        console.warn('[DEBUG useFormulario] Label .is-required sem atributo "for":', label.innerText)
        return
      }

      const inputElement = document.getElementById(inputId)
      if (!inputElement) {
        console.warn(`[DEBUG useFormulario] Elemento input não encontrado para ID: ${inputId} (Label: ${label.innerText})`)
        return
      }

      let isEmpty = false

      if (inputElement.tagName === 'SELECT') {
        isEmpty = !inputElement.value || inputElement.value === ''
      } else {
        isEmpty = !inputElement.value || inputElement.value.trim() === ''
      }

      console.log(`[DEBUG useFormulario] Campo: ${inputId}, Valor: "${inputElement.value}", Vazio: ${isEmpty}`)

      if (isEmpty) {
        const nomeCampoLimpo = label.innerText.replace('*', '').trim()
        frontendErrors[inputId] = `O campo "${nomeCampoLimpo}" é de preenchimento obrigatório.`
        isValid = false
      }
    })

    if (!isValid) {
      errors.value = frontendErrors
    }

    return isValid
  }

  const submitForm = async (apiPath, idKey, payloadFromComponent = null) => {
    if (isLoading.value) return false

    errors.value = {}

    // GUARDIÃO: Intercepta o robô e puxa a mensagem limpa e dinâmica para a notificação
    if (!validarCamposObrigatoriosFrontEnd()) {
      const primeiraMensagemErro = Object.values(errors.value)[0] || 'Por favor, preencha todos os campos obrigatórios destacados em vermelho.'

      emit('show-notification', { message: primeiraMensagemErro, type: 'warning' })
      await focarPrimeiroErro()
      return false
    }

    isLoading.value = true
    console.log('[DEBUG useFormulario] Tentando requisição POST/PUT...')

    const dataToSend = payloadFromComponent || item.value

    try {
      let response

      if (isEditing.value) {
        const id = item.value[idKey]
        if (!id) throw new Error('ID não encontrado para a operação PUT.')
        response = await api.put(`/${apiPath}/${id}`, dataToSend)
      } else {
        response = await api.post(`/${apiPath}`, dataToSend)
      }

      item.value = response.data
      emit('itemAtualizado', response.data)

      originalItemJson = JSON.stringify(item.value)
      hasChanges.value = false
      emit('changesDetected', false)

      return true
    } catch (error) {
      console.error(`Erro ao salvar ${apiPath}:`, error)
      let mensagemErro = `Não foi possível salvar ${apiPath}.`
      const emitirErro = true

      if (error.response?.status === 401) {
        mensagemErro = 'Sua sessão expirou ou é inválida. Tente fazer login novamente.'
      } else if (error.response?.status === 409) {
        const mensagem = error.response?.data?.message ||
          'Conflito: outro usuário alterou este registro. Recarregando...'
        emit('show-notification', { message: mensagem, type: 'warning' })
        setTimeout(() => window.location.reload(), 3000)
        return false
      } else if (error.response?.status === 400 && error.response.data?.errors) {
        const backendErrors = error.response.data.errors
        const normalizedErrors = {}

        for (const [key, msgs] of Object.entries(backendErrors)) {
          const cleanKey = key.replace(/^(?:\$\.|model\.|item\.)/i, '')
          const camelKey = cleanKey.charAt(0).toLowerCase() + cleanKey.slice(1)
          normalizedErrors[camelKey] = msgs[0]
        }

        errors.value = { ...errors.value, ...normalizedErrors }

        const mensagensOriginais = Object.values(backendErrors).flat()
        const primeiroErro = mensagensOriginais[0]

        const match = primeiroErro.match(/The (.+) field is required/i)
        if (match) {
          const campoBackend = match[1]
          const labelFrontend = mapaCampos[campoBackend] || campoBackend.replace(/^Lst/, '')
          mensagemErro = `O campo "${labelFrontend}" falhou na validação de servidor.`
        } else {
          mensagemErro = primeiroErro
        }
      } else if (error.response?.data && typeof error.response.data === 'string') {
        mensagemErro = error.response.data
      } else if (error.response?.data?.detail) {
        mensagemErro = error.response.data.detail
      } else if (error.response?.data?.message) {
        mensagemErro = error.response.data.message
      } else if (error.response?.data?.title) {
        mensagemErro = error.response.data.title
      }

      if (emitirErro) {
        emit('show-notification', { message: mensagemErro, type: 'error' })
      }

      await focarPrimeiroErro()

      return false
    } finally {
      isLoading.value = false
    }
  }

  const handleCancel = () => {
    emit('cancelar')
  }

  return {
    item,
    isLoading,
    isEditing,
    hasChanges,
    entityInfo,
    errors,
    submitForm,
    handleCancel,
    focarPrimeiroErro,
    initializeForm,
    validarCamposObrigatoriosFrontEnd
  }
}
